import * as Sentry from '@sentry/react';

import { ComplianceStatus } from '@/warranty/domain/entities/compliance-status';
import type { WarrantyIDStatus } from '@/warranty/domain/entities/warranty-id-status';
import { WarrantyCoverageStatus } from '@/warranty/domain/entities/warranty-status';

import { DIM_BY_LEVEL } from '../../constants/battery-levels';

import type { AggregationLevel } from './utils';

export interface WarrantyIDStatusRow {
  // dynamic id fields; we don't enumerate every level column
  [key: string]: string | number | undefined;
  'platform_core_warranty_unit_status_all_levels.total_age': number;
  'platform_core_warranty_unit_status_all_levels.compliance_status': string;
  'platform_core_warranty_unit_status_all_levels.guarantee_status': string;
  'platform_core_warranty_unit_status_all_levels.total_throughput': number;
  'platform_core_warranty_unit_status_all_levels.total_cycles_max': number;
  'platform_core_warranty_unit_status_all_levels.soh_accure_agg': number;
  'platform_core_warranty_unit_status_all_levels.penalty_max': number;
  'platform_core_warranty_unit_status_all_levels.soh_warranty_avg': number;
  'platform_core_warranty_unit_status_all_levels.coverage_issues_new': number;
  'platform_core_warranty_unit_status_all_levels.compliance_issues_new': number;
  'platform_xfleet_warranty_case_alerts_future.value_avg': number;
  'platform_xfleet_warranty_case_alerts_future.threshold_avg': number;
}

export function getWarrantyStatusQueryFields(
  level: AggregationLevel,
): string[] {
  const idField = level === 'site' ? DIM_BY_LEVEL.site : DIM_BY_LEVEL[level];

  return [
    idField,
    'platform_core_warranty_unit_status_all_levels.total_age',
    'platform_core_warranty_unit_status_all_levels.compliance_status',
    'platform_core_warranty_unit_status_all_levels.guarantee_status',
    'platform_core_warranty_unit_status_all_levels.total_throughput',
    'platform_core_warranty_unit_status_all_levels.total_cycles_max',
    'platform_core_warranty_unit_status_all_levels.soh_accure_agg',
    'platform_core_warranty_unit_status_all_levels.penalty_max',
    'platform_core_warranty_unit_status_all_levels.soh_warranty_avg',
    'platform_core_warranty_unit_status_all_levels.coverage_issues_new',
    'platform_core_warranty_unit_status_all_levels.compliance_issues_new',
    'platform_xfleet_warranty_case_alerts_future.value_avg',
    'platform_xfleet_warranty_case_alerts_future.threshold_avg',
  ];
}

/**
 * Apply customer-specific data transformations
 * @param warrantyStatus The warranty status object
 * @param customerId The customer ID
 * @param level The aggregation level
 * @returns Modified warranty status object
 */
export function applyCustomerSpecificTransformations(
  warrantyStatus: WarrantyIDStatus,
  customerId: string,
  level: AggregationLevel,
): WarrantyIDStatus {
  // Clone the object to avoid mutations
  const result = { ...warrantyStatus };

  // Customer 69: Set throughput to 0 for container level
  // This will display as N/A in the UI
  if (customerId === '69' && level === 'container') {
    result.throughputTotal = null;
    // set status to -1
    result.warrantyCoverageStatus = WarrantyCoverageStatus.NO_DATA;
    result.complianceStatus = ComplianceStatus.NO_DATA;
  }

  return result;
}

export function transformWarrantyStatusResult(
  result: WarrantyIDStatusRow,
  level: AggregationLevel,
): WarrantyIDStatus {
  // Extract the appropriate ID based on level
  const id = extractIdFromResult(result as any, level);
  const fullFrontendId = extractFullFrontendIdFromResult(result as any, level);

  return {
    id: id || '', // Ensure id is never undefined
    groupFullId: '', // TODO: remove
    topFullId: '', // TODO: remove
    unitFullId: '', // TODO: remove
    fullFrontendId: fullFrontendId || '', // Ensure fullFrontendId is never undefined
    warrantyCoverageStatus: warrantyCoverageStatusFromLooker(
      result['platform_core_warranty_unit_status_all_levels.guarantee_status'],
    ),
    complianceStatus: complianceStatusFromLooker(
      result['platform_core_warranty_unit_status_all_levels.compliance_status'],
    ),
    dataStatus: 1,
    ageInYears:
      result['platform_core_warranty_unit_status_all_levels.total_age'] / 365,
    throughputTotal:
      result['platform_core_warranty_unit_status_all_levels.total_throughput'] *
      1_000_000,
    cyclesTotal:
      result['platform_core_warranty_unit_status_all_levels.total_cycles_max'],
    suggestedAction: '', // FIXME:
    sohAccure:
      result['platform_core_warranty_unit_status_all_levels.soh_accure_agg'],
    sohPenaltyMax:
      result['platform_core_warranty_unit_status_all_levels.penalty_max'],
    sohGuaranteed:
      result['platform_core_warranty_unit_status_all_levels.soh_warranty_avg'],
    nUnitsPerformanceIssues:
      result[
        'platform_core_warranty_unit_status_all_levels.coverage_issues_new'
      ],
    nUnitsOperationIssues:
      result[
        'platform_core_warranty_unit_status_all_levels.compliance_issues_new'
      ],
    availability: {
      value: result['platform_xfleet_warranty_case_alerts_future.value_avg'],
      threshold:
        result['platform_xfleet_warranty_case_alerts_future.threshold_avg'],
    },
  };
}

function extractIdFromResult(
  result: Record<string, string | undefined>,
  level: AggregationLevel,
): string {
  const field = level === 'site' ? DIM_BY_LEVEL.site : DIM_BY_LEVEL[level];
  return result[field] || '';
}

function extractFullFrontendIdFromResult(
  result: Record<string, string | undefined>,
  level: AggregationLevel,
): string {
  const field = level === 'site' ? DIM_BY_LEVEL.site : DIM_BY_LEVEL[level];
  return result[field] || '';
}

function complianceStatusFromLooker(status: string): ComplianceStatus {
  switch (status) {
    case 'Compliant':
      return ComplianceStatus.COMPLIANT;
    case 'Non-Compliant':
    case 'At Risk':
      return ComplianceStatus.NON_COMPLIANT;
    case 'No Data':
      return ComplianceStatus.NO_DATA;
    // case 'At Risk':
    //   return ComplianceStatus.AT_RISK;
    default: {
      Sentry.captureException(
        new Error('Unknown compliance status: ' + status),
      );
      return '' as any as ComplianceStatus;
    }
  }
}

function warrantyCoverageStatusFromLooker(
  status: string,
): WarrantyCoverageStatus {
  switch (status) {
    case 'Valid':
      return WarrantyCoverageStatus.VALID;
    case 'Case Open':
      return WarrantyCoverageStatus.CASE_OPEN;
    case 'Expired':
      return WarrantyCoverageStatus.EXPIRED;
    case 'No Data':
      return WarrantyCoverageStatus.NO_DATA;
    default: {
      Sentry.captureException(
        new Error('Unknown warranty coverage status: ' + status),
      );
      return '' as any as WarrantyCoverageStatus;
    }
  }
}
